/**
 * CartSync.js - Guest Cart Management Utility
 * 
 * Uses sessionStorage to persist cart data during browsing session.
 * Cart is automatically cleared when browser window is closed.
 */

const GUEST_CART_KEY = 'sirifarms_guest_cart';

/**
 * Get guest cart items from sessionStorage
 * @returns {Array} Array of cart items or empty array
 */
export const getGuestCart = () => {
    try {
        const cart = sessionStorage.getItem(GUEST_CART_KEY);
        return cart ? JSON.parse(cart) : [];
    } catch (error) {
        console.error('Error reading guest cart:', error);
        return [];
    }
};

/**
 * Save guest cart items to sessionStorage
 * @param {Array} items - Array of cart items
 */
export const setGuestCart = (items) => {
    try {
        sessionStorage.setItem(GUEST_CART_KEY, JSON.stringify(items));
    } catch (error) {
        console.error('Error saving guest cart:', error);
    }
};

/**
 * Clear guest cart from sessionStorage (used after merge)
 */
export const clearGuestCart = () => {
    try {
        sessionStorage.removeItem(GUEST_CART_KEY);
    } catch (error) {
        console.error('Error clearing guest cart:', error);
    }
};

/**
 * Add item to guest cart
 * Handles duplicate products by updating quantity
 * @param {Object} item - Cart item with productId, size, quantity, product details
 * @returns {Array} Updated cart items
 */
export const addGuestCartItem = (item) => {
    const cart = getGuestCart();

    // Check if same product with same size already exists
    const existingIndex = cart.findIndex(
        (cartItem) =>
            cartItem.productId === item.productId &&
            cartItem.size === item.size
    );

    if (existingIndex !== -1) {
        // Update existing item quantity
        const newQuantity = cart[existingIndex].quantity + item.quantity;

        // Check bulk limit (10 boxes per product type)
        if (newQuantity > 10) {
            return {
                success: false,
                error: 'BULK_LIMIT_EXCEEDED',
                message: 'For bulk orders exceeding 10 boxes, please contact Siri Farms directly via our About Us page.',
                cart
            };
        }

        cart[existingIndex].quantity = newQuantity;
        cart[existingIndex].price = calculateItemPrice(cart[existingIndex]);
        cart[existingIndex].discountedPrice = calculateItemDiscountedPrice(cart[existingIndex]);
    } else {
        // Check bulk limit for new item
        if (item.quantity > 10) {
            return {
                success: false,
                error: 'BULK_LIMIT_EXCEEDED',
                message: 'For bulk orders exceeding 10 boxes, please contact Siri Farms directly via our About Us page.',
                cart
            };
        }

        // Add new item with calculated prices
        const newItem = {
            ...item,
            id: `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            price: calculateItemPrice(item),
            discountedPrice: calculateItemDiscountedPrice(item),
        };
        cart.push(newItem);
    }

    setGuestCart(cart);
    return { success: true, cart };
};

/**
 * Remove item from guest cart
 * @param {string|number} productId - Product ID
 * @param {string} size - Size/box type
 * @returns {Array} Updated cart items
 */
export const removeGuestCartItem = (productId, size) => {
    const cart = getGuestCart();
    const updatedCart = cart.filter(
        (item) => !(item.productId === productId && item.size === size)
    );
    setGuestCart(updatedCart);
    return updatedCart;
};

/**
 * Remove item from guest cart by item ID
 * @param {string} itemId - Guest cart item ID
 * @returns {Array} Updated cart items
 */
export const removeGuestCartItemById = (itemId) => {
    const cart = getGuestCart();
    const updatedCart = cart.filter((item) => item.id !== itemId);
    setGuestCart(updatedCart);
    return updatedCart;
};

/**
 * Update item quantity in guest cart
 * @param {string|number} productId - Product ID
 * @param {string} size - Size/box type
 * @param {number} quantity - New quantity
 * @returns {Object} Result with success status and cart
 */
export const updateGuestCartItem = (productId, size, quantity) => {
    const cart = getGuestCart();
    const itemIndex = cart.findIndex(
        (item) => item.productId === productId && item.size === size
    );

    if (itemIndex === -1) {
        return { success: false, error: 'ITEM_NOT_FOUND', cart };
    }

    // Check bulk limit
    if (quantity > 10) {
        return {
            success: false,
            error: 'BULK_LIMIT_EXCEEDED',
            message: 'For bulk orders exceeding 10 boxes, please contact Siri Farms directly via our About Us page.',
            cart
        };
    }

    if (quantity <= 0) {
        // Remove item if quantity is 0 or less
        return {
            success: true,
            cart: removeGuestCartItem(productId, size)
        };
    }

    cart[itemIndex].quantity = quantity;
    cart[itemIndex].price = calculateItemPrice(cart[itemIndex]);
    cart[itemIndex].discountedPrice = calculateItemDiscountedPrice(cart[itemIndex]);

    setGuestCart(cart);
    return { success: true, cart };
};

/**
 * Update item quantity by item ID
 * @param {string} itemId - Guest cart item ID
 * @param {number} quantity - New quantity
 * @returns {Object} Result with success status and cart
 */
export const updateGuestCartItemById = (itemId, quantity) => {
    const cart = getGuestCart();
    const itemIndex = cart.findIndex((item) => item.id === itemId);

    if (itemIndex === -1) {
        return { success: false, error: 'ITEM_NOT_FOUND', cart };
    }

    // Check bulk limit
    if (quantity > 10) {
        return {
            success: false,
            error: 'BULK_LIMIT_EXCEEDED',
            message: 'For bulk orders exceeding 10 boxes, please contact Siri Farms directly via our About Us page.',
            cart
        };
    }

    if (quantity <= 0) {
        return {
            success: true,
            cart: removeGuestCartItemById(itemId)
        };
    }

    cart[itemIndex].quantity = quantity;
    cart[itemIndex].price = calculateItemPrice(cart[itemIndex]);
    cart[itemIndex].discountedPrice = calculateItemDiscountedPrice(cart[itemIndex]);

    setGuestCart(cart);
    return { success: true, cart };
};

/**
 * Get box weight in pounds based on size
 * @param {string} size - Size/box type name
 * @returns {number} Weight in pounds
 */
export const getBoxWeight = (size) => {
    const weights = {
        'One Unit': 1,
        'Small Box': 5,
        'Large Box': 15,
        'XL Box': 25,
        'Bulk Box': 40,
    };
    return weights[size] || 1;
};

/**
 * Calculate item price based on per-pound price and box weight
 * @param {Object} item - Cart item with product price info
 * @returns {number} Calculated price
 */
export const calculateItemPrice = (item) => {
    const weight = getBoxWeight(item.size);
    const pricePerLb = item.product?.price || item.pricePerLb || 0;
    return pricePerLb * weight * item.quantity;
};

/**
 * Calculate item discounted price based on per-pound price and box weight
 * @param {Object} item - Cart item with product price info
 * @returns {number} Calculated discounted price
 */
export const calculateItemDiscountedPrice = (item) => {
    const weight = getBoxWeight(item.size);
    const discountedPricePerLb = item.product?.discountedPrice || item.discountedPricePerLb || item.product?.price || item.pricePerLb || 0;
    return discountedPricePerLb * weight * item.quantity;
};

/**
 * Calculate guest cart totals
 * @returns {Object} Cart totals including totalPrice, totalDiscountedPrice, totalItems
 */
export const getGuestCartTotals = () => {
    const cart = getGuestCart();

    return cart.reduce((totals, item) => {
        return {
            totalPrice: totals.totalPrice + (item.price || 0),
            totalDiscountedPrice: totals.totalDiscountedPrice + (item.discountedPrice || 0),
            totalItem: totals.totalItem + item.quantity,
        };
    }, { totalPrice: 0, totalDiscountedPrice: 0, totalItem: 0 });
};

/**
 * Check if user is in guest mode (no JWT)
 * @returns {boolean} True if guest mode
 */
export const isGuestMode = () => {
    return !localStorage.getItem('jwt');
};

/**
 * Prepare guest cart items for merge API call
 * @returns {Array} Array of items formatted for backend merge endpoint
 */
export const getGuestCartForMerge = () => {
    const cart = getGuestCart();
    return cart.map((item) => ({
        productId: item.productId,
        size: item.size,
        quantity: item.quantity,
    }));
};

const CartSync = {
    getGuestCart,
    setGuestCart,
    clearGuestCart,
    addGuestCartItem,
    removeGuestCartItem,
    removeGuestCartItemById,
    updateGuestCartItem,
    updateGuestCartItemById,
    getBoxWeight,
    calculateItemPrice,
    calculateItemDiscountedPrice,
    getGuestCartTotals,
    isGuestMode,
    getGuestCartForMerge,
};

export default CartSync;
